import { apiClient } from './apiClient';
import { httpErrorDetailMessage } from '../utils/httpErrorDetail';
import {
  formatPersistModeNotPatchMessage,
  normalizeControlBridgePersistMode,
  normalizeControlBridgeSotPlane,
  type ControlBridgePersistMode,
  type ControlBridgeSotPlane,
} from './controlBridgePersistMode';

export type IssueTransitionAction = 'advance' | 'close';

export interface IssueTransitionRequest {
  itemId: string;
  action: IssueTransitionAction;
}

export interface IssueTransitionWriteBase {
  artifactItemCount?: number;
  itemIdSetHash?: string;
}

export interface IssueTransitionResponse {
  ok: boolean;
  itemId: string;
  action: IssueTransitionAction;
  fromState?: string;
  toState?: string;
  workspaceSlug?: string;
  persistMode?: ControlBridgePersistMode;
  sotPlane?: ControlBridgeSotPlane;
  source?: 'db' | 'workspace';
  writeBase?: IssueTransitionWriteBase;
}

export class ControlBridgeIssueTransitionError extends Error {
  constructor(
    message: string,
    readonly code?: string,
    readonly httpStatus?: number
  ) {
    super(message);
    this.name = 'ControlBridgeIssueTransitionError';
  }
}

/** F2 / U2 / F5: issue-transition must land as Postgres patch on hosted-SoT. */
export function assertIssueTransitionPersistModePatch(
  response: IssueTransitionResponse
): void {
  if (response.persistMode === 'patch') return;
  throw new ControlBridgeIssueTransitionError(
    formatPersistModeNotPatchMessage(
      'Issue-transition',
      response.persistMode,
      response.sotPlane
    ),
    'PERSIST_MODE_NOT_PATCH'
  );
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

function normalizeWriteBase(raw: unknown): IssueTransitionWriteBase | undefined {
  const obj = asRecord(raw);
  if (!obj) return undefined;
  const count = obj.artifactItemCount ?? obj.artifact_item_count;
  const hash = obj.itemIdSetHash ?? obj.item_id_set_hash;
  const out: IssueTransitionWriteBase = {};
  if (typeof count === 'number') out.artifactItemCount = count;
  if (typeof hash === 'string') out.itemIdSetHash = hash;
  return Object.keys(out).length ? out : undefined;
}

export function normalizeIssueTransitionResponse(
  data: unknown,
  fallback: { itemId: string; action: IssueTransitionAction }
): IssueTransitionResponse {
  const row = asRecord(data) ?? {};
  const persistMode = normalizeControlBridgePersistMode(row.persistMode ?? row.persist_mode);
  const sourceRaw = row.source;
  const actionRaw = row.action;
  const action: IssueTransitionAction =
    actionRaw === 'advance' || actionRaw === 'close' ? actionRaw : fallback.action;
  return {
    ok: row.ok !== false,
    itemId:
      (typeof row.itemId === 'string' && row.itemId) ||
      (typeof row.item_id === 'string' && row.item_id) ||
      fallback.itemId,
    action,
    fromState:
      (typeof row.fromState === 'string' && row.fromState) ||
      (typeof row.from_state === 'string' ? row.from_state : undefined),
    toState:
      (typeof row.toState === 'string' && row.toState) ||
      (typeof row.to_state === 'string' ? row.to_state : undefined),
    workspaceSlug:
      (typeof row.workspaceSlug === 'string' && row.workspaceSlug) ||
      (typeof row.workspace_slug === 'string' ? row.workspace_slug : undefined),
    persistMode,
    sotPlane: normalizeControlBridgeSotPlane(row.sotPlane ?? row.sot_plane),
    source: sourceRaw === 'db' || sourceRaw === 'workspace' ? sourceRaw : undefined,
    writeBase: normalizeWriteBase(row.writeBase ?? row.write_base ?? row.write_base_model),
  };
}

/** Advance one operational step, or close (→ CLOSED) an issue. */
export async function transitionControlBridgeIssue(
  workspaceSlug: string,
  request: IssueTransitionRequest
): Promise<IssueTransitionResponse> {
  const slug = workspaceSlug.trim();
  const itemId = request.itemId.trim();
  if (!slug || !itemId) {
    throw new ControlBridgeIssueTransitionError(
      'Workspace slug and item id are required',
      'NOT_CONFIGURED'
    );
  }

  try {
    const { data } = await apiClient.post<unknown>(
      `/kryspace/workspace/${encodeURIComponent(slug)}/issue-transition`,
      {
        itemId,
        action: request.action,
      }
    );
    const normalized = normalizeIssueTransitionResponse(data, {
      itemId,
      action: request.action,
    });
    assertIssueTransitionPersistModePatch(normalized);
    return normalized;
  } catch (err: unknown) {
    if (err instanceof ControlBridgeIssueTransitionError) {
      throw err;
    }
    const axiosErr = err as {
      response?: { status?: number; data?: { detail?: unknown } };
      message?: string;
    };
    const detail = axiosErr.response?.data?.detail;
    const status = axiosErr.response?.status;
    const message =
      status === 409
        ? 'Workspace changed while updating — refresh and try again'
        : httpErrorDetailMessage(detail, axiosErr.message || 'Failed to transition issue');
    throw new ControlBridgeIssueTransitionError(
      axiosErr.response?.status === 404 && !detail
        ? 'Issue-transition API is not available on this server (restart backend after deploy)'
        : message,
      axiosErr.response?.status === 404 && !detail
        ? 'ROUTE_NOT_FOUND'
        : axiosErr.response?.status === 409
          ? 'WRITE_BASE_STALE'
          : 'REQUEST_FAILED',
      axiosErr.response?.status
    );
  }
}
